import {z} from 'zod';
import {ObjectId} from 'bson';
import {zodTypeguard} from '../api/helpers';
import {isObjectId, ObjectIdSchema} from '../zod';
import {AccountTierSchema, IntentTierSchema} from '../scoredAccounts';
import {
  BusinessAudienceSchema,
  PrimaryBusinessCategorySchema,
} from '../account';
import {CountryCodeSchema} from '../countries';
import {SubdivisionCodeSchema} from '../subdivisions';
import {ScoringSignalSchema} from '../signals';
import {AccountFieldOrCustomFieldSchema} from '../signalDefinition';
import {
  createFilterConditionsSchema,
  RangeValueSchema,
  SharedRangeFields,
} from './base';

// ============================================================================
// AccountFilter - for querying scoredAccounts collection (customer-specific)
// ============================================================================

// Account view enum
export const AccountViewSchema = z.enum([
  'all',
  'saved',
  'crm',
  'imported',
  'intent',
  'recommended',
]);
export type AccountView = z.infer<typeof AccountViewSchema>;

// Value types for 'in' operator
const InValueSchema = z.union([
  z.array(ObjectIdSchema), // tags, similarTo
  z.array(AccountTierSchema), // tier
  z.array(IntentTierSchema), // intentTier
  z.array(BusinessAudienceSchema), // audience
  z.array(PrimaryBusinessCategorySchema), // primaryCategory
  z.array(ScoringSignalSchema), // signals
  z.array(AccountFieldOrCustomFieldSchema), // matchedIntentFields
  z.array(CountryCodeSchema), // hqCountry
  z.array(SubdivisionCodeSchema), // hqState
]);

// Filter condition - discriminated union by operator
export const AccountFilterConditionSchema = z.discriminatedUnion('operator', [
  // Boolean equals/notEquals
  z.object({
    operator: z.literal('equals'),
    field: z.enum([
      'active',
      'disqualified',
      'excluded',
      'saved',
      'imported',
      'inCRM',
      'inSAM',
    ]),
    value: z.boolean(),
    negate: z.boolean().optional(), // if true, becomes notEquals
  }),

  // Array in/nin
  // TODO: add 'secondaryCategory' and 'locationCountries' to match GlobalAccountFilter
  z.object({
    operator: z.literal('in'),
    field: z.enum([
      'tags',
      'tier',
      'intentTier',
      'audience',
      'primaryCategory',
      'similarTo',
      'signals',
      'matchedIntentFields',
      'hqCountry',
      'hqState',
    ]),
    value: InValueSchema,
    negate: z.boolean().optional(), // if true, becomes nin
  }),

  // Exists/notExists
  z.object({
    operator: z.literal('exists'),
    field: z.enum(['intent']),
    negate: z.boolean().optional(), // if true, becomes mustNot exists
  }),

  // Range (no negation support) - uses shared range fields and value schema
  z.object({
    operator: z.literal('range'),
    field: z.enum(SharedRangeFields),
    value: RangeValueSchema,
  }),
]);
export type AccountFilterCondition = z.infer<
  typeof AccountFilterConditionSchema
>;

const AccountFilterGroupSchema = z.object({
  operator: z.enum(['and', 'or']),
  conditions: z.array(AccountFilterConditionSchema),
});
export type AccountFilterGroup = z.infer<typeof AccountFilterGroupSchema>;
export const isAccountFilterGroup = zodTypeguard(AccountFilterGroupSchema);

// Conditions wrapper using shared factory
const AccountFilterConditionsSchema = createFilterConditionsSchema(
  AccountFilterConditionSchema
);

// Extract item type from the conditions schema
export type AccountFilterItem = z.infer<
  typeof AccountFilterConditionsSchema
>['items'][number];

// Top-level filter structure
export const AccountFilterSchema = z.object({
  // Filter items (mix of groups and single conditions) - uses shared factory
  conditions: AccountFilterConditionsSchema.optional(),

  // Top-level filters (always ANDed with conditions)
  view: AccountViewSchema.optional(),
  excluded: z.boolean().optional(),
  disqualified: z.boolean().optional(),
  search: z.string().optional(),
});
export type AccountFilter = z.infer<typeof AccountFilterSchema>;

export function createAccountFilter(
  conditions: Array<AccountFilterCondition | AccountFilterGroup>,
  options?: {
    operator?: 'and' | 'or';
    view?: AccountView;
    excluded?: boolean;
    disqualified?: boolean;
    search?: string;
  }
): AccountFilter {
  const {
    operator = 'and',
    view,
    excluded,
    disqualified,
    search,
  } = options ?? {};

  return {
    conditions: {
      operator,
      items: conditions.map((item): AccountFilterItem => {
        if (isAccountFilterGroup(item)) {
          return {
            type: 'group',
            operator: item.operator,
            conditions: item.conditions,
          };
        } else {
          return {
            type: 'condition',
            condition: item,
          };
        }
      }),
    },
    ...(view ? {view} : {}),
    ...(excluded !== undefined ? {excluded} : {}),
    ...(disqualified !== undefined ? {disqualified} : {}),
    ...(search ? {search} : {}),
  };
}

/**
 * Walk an AccountFilter's conditions to collect all tag ObjectIds referenced in 'tags' conditions.
 */
export function extractTagIdsFromFilter(filter: AccountFilter): ObjectId[] {
  if (!filter.conditions) {
    return [];
  }

  return filter.conditions.items.flatMap(collectTagIdsFromItem);
}

function collectTagIdsFromItem(item: AccountFilterItem): ObjectId[] {
  const filterObjectIds = (value: unknown[]) => value.filter(isObjectId);

  if (item.type === 'condition') {
    if (item.condition.field === 'tags') {
      return filterObjectIds(item.condition.value);
    }
  } else {
    return item.conditions.flatMap((condition) =>
      condition.field === 'tags' ? filterObjectIds(condition.value) : []
    );
  }

  return [];
}

// Convert AiEnrichmentAccountFilter to AccountFilter format for queries
export function aiEnrichmentFilterToAccountFilter(
  filter:
    | {active: true; excluded: false}
    | {active: true; excluded: false; tags: ObjectId[]}
): AccountFilter {
  return createAccountFilter([
    {operator: 'equals', field: 'active', value: true},
    {operator: 'equals', field: 'excluded', value: false},
    ...('tags' in filter
      ? [{operator: 'in' as const, field: 'tags' as const, value: filter.tags}]
      : []),
  ]);
}
